import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isNativeShell } from './platform.js';

/**
 * Ponte com a parte nativa do app (Android): avisos montados no aparelho, com
 * o texto da mensagem e as ações Responder, Marcar como lida e Silenciar.
 * Fora do app instalado, nada aqui faz efeito.
 */
type NativePlugin = {
  ensurePreviewKey(): Promise<{ publicKey: string }>;
  removePreviewKey(): Promise<void>;
  setSession(options: {
    homeserverUrl: string;
    accessToken: string;
    privateReceipts: boolean;
    ownerName: string;
    /** Endereço da API: os botões dos avisos do Secretário resolvem por ele (T7.29). */
    apiBase?: string;
  }): Promise<void>;
  clearSession(): Promise<void>;
  setMutedRooms(options: { rooms: string[] }): Promise<void>;
  getMutedRooms(): Promise<{ rooms: string[] }>;
  setRoomNames(options: { names: Record<string, string> }): Promise<void>;
  setShareTargets(options: { rooms: { id: string; name: string }[] }): Promise<void>;
  clearRoom(options: { roomId: string }): Promise<void>;
  /** Play In-App Updates: o que a loja diz sobre versão nova (só app instalado pela Play). */
  checkUpdate(): Promise<{ available: boolean; versionCode: number | null; installedCode: number }>;
  /** Abre a atualização da Play por dentro do app; sem ela, a página do app na loja. */
  startUpdate(): Promise<{ started: boolean }>;
  /** Tem música tocando no aparelho (só o fato). */
  musicActive(): Promise<{ active: boolean }>;
  /** Volume da conversa ao vivo acompanhando o volume de mídia do Android. */
  volumeSync(options: { on: boolean }): Promise<void>;
  /** Envio em lote andando: o aviso fixo com a contagem e os botões (vales do servidor). */
  outreachShow(options: {
    summary: OutreachDeviceSummary;
    grants?: Record<string, string>;
  }): Promise<void>;
  outreachClear(): Promise<void>;
  addListener(
    event: 'notificationOpen' | 'roomMuted',
    listener: (data: { roomId: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'shareReceived',
    listener: (data: { text: string; roomId?: string | null; files?: NativeSharedFile[] }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'noticeOpen',
    listener: (data: { href: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'notificationReply',
    listener: (data: { roomId: string; text: string }) => void,
  ): Promise<PluginListenerHandle>;
};

/** O que o aviso fixo do envio mostra (o mesmo `deviceSummary` do servidor). */
export type OutreachDeviceSummary = {
  id: string;
  title: string;
  state: string;
  sent: number;
  reach: number;
  waSent: number;
  waTotal: number;
  next: string | null;
  readyAt: number;
  hold: string;
  open: string | null;
};

/** Arquivo que outro app compartilhou, já copiado para a pasta temporária do app. */
export type NativeSharedFile = {
  name: string;
  mime: string;
  path?: string;
  size?: number;
  tooLarge?: boolean;
  unreadable?: boolean;
};

const plugin = registerPlugin<NativePlugin>('SecretarioNative');

export function nativeNotices(): boolean {
  return isNativeShell() && Capacitor.getPlatform() === 'android';
}

/** Chama a parte nativa sem deixar erro dela derrubar a tela. */
async function quiet<T>(call: () => Promise<T>): Promise<T | null> {
  if (!nativeNotices()) return null;
  try {
    return await call();
  } catch {
    return null;
  }
}

export const nativeBridge = {
  previewKey: () =>
    quiet(() => plugin.ensurePreviewKey()).then((result) => result?.publicKey ?? null),
  removePreviewKey: () => quiet(() => plugin.removePreviewKey()),
  setSession: (options: Parameters<NativePlugin['setSession']>[0]) =>
    quiet(() => plugin.setSession(options)),
  clearSession: () => quiet(() => plugin.clearSession()),
  setMutedRooms: (rooms: string[]) => quiet(() => plugin.setMutedRooms({ rooms })),
  setRoomNames: (names: Record<string, string>) => quiet(() => plugin.setRoomNames({ names })),
  setShareTargets: (rooms: { id: string; name: string }[]) =>
    quiet(() => plugin.setShareTargets({ rooms })),
  mutedRooms: () => quiet(() => plugin.getMutedRooms()).then((result) => result?.rooms ?? null),
  clearRoom: (roomId: string) => quiet(() => plugin.clearRoom({ roomId })),
  checkUpdate: () => quiet(() => plugin.checkUpdate()),
  startUpdate: () => quiet(() => plugin.startUpdate()).then((result) => result?.started ?? false),
  musicActive: () => quiet(() => plugin.musicActive()).then((result) => result?.active === true),
  volumeSync: (on: boolean) => quiet(() => plugin.volumeSync({ on })),
  outreachShow: (summary: OutreachDeviceSummary, grants?: Record<string, string>) =>
    quiet(() => plugin.outreachShow({ summary, ...(grants ? { grants } : {}) })),
  outreachClear: () => quiet(() => plugin.outreachClear()),
  /** O que a pessoa fez num aviso: abriu a conversa, respondeu ou silenciou. */
  async watch(handlers: {
    onOpen(roomId: string): void;
    onReply(roomId: string, text: string): void;
    onMuted(roomId: string): void;
    /** Toque num aviso do Secretário (consulta, conta, teste): a tela dele. */
    onNotice(href: string): void;
    /** Algo compartilhado de outro app; `roomId` quando veio do atalho de uma conversa. */
    onShare(text: string, roomId: string | null, files: NativeSharedFile[]): void;
  }): Promise<() => void> {
    if (!nativeNotices()) return () => {};
    try {
      const handles = await Promise.all([
        plugin.addListener('notificationOpen', (data) => handlers.onOpen(data.roomId)),
        plugin.addListener('notificationReply', (data) => handlers.onReply(data.roomId, data.text)),
        plugin.addListener('roomMuted', (data) => handlers.onMuted(data.roomId)),
        plugin.addListener('noticeOpen', (data) =>
          handlers.onNotice(data.href?.startsWith('/') ? data.href : '/'),
        ),
        plugin.addListener('shareReceived', (data) =>
          handlers.onShare(data.text ?? '', data.roomId ?? null, data.files ?? []),
        ),
      ]);
      return () => {
        for (const handle of handles) void handle.remove();
      };
    } catch {
      return () => {};
    }
  },
};
